import React, { Component, ErrorInfo, ReactNode } from 'react';
import { AlertCircle } from 'lucide-react';
import { Button } from './ui/button';
import { BrandedLoader } from './ui/BrandedLoader';
import { captureError, addBreadcrumb } from '@/lib/error-monitor';
import { isChunkLoadError, reloadForFreshChunks } from '@/lib/chunk-reload';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  // A failed lazy-chunk import (a still-open tab on a build that has since been
  // redeployed) throws into this boundary exactly like any other render error.
  // We flag it so render() can show the ordinary branded loader while a reload
  // fetches the fresh assets, instead of flashing the alarming "Something went
  // wrong" page on every post-deploy navigation. See src/lib/chunk-reload.
  isChunkError: boolean;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null, isChunkError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    // Classify chunk-vs-real here (this is pure) so the very FIRST fallback
    // commit already knows which UI to render. Deciding it in componentDidCatch
    // instead would paint the scary error page for one frame before correcting
    // itself — which is exactly the flash we're removing.
    return { hasError: true, error, isChunkError: isChunkLoadError(error) };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    if (isChunkLoadError(error)) {
      // Expected, recoverable "you're on an old build" condition — not a crash.
      // Don't log it to the error monitor; just reload once to pick up the
      // fresh asset manifest (chunk-reload de-dupes with a cooldown).
      const reloading = reloadForFreshChunks();
      if (!reloading) {
        // Cooldown hit: we already reloaded moments ago and the chunk STILL
        // won't load (genuinely broken deploy / offline). Drop the loader and
        // fall through to the actionable error page rather than spin forever.
        this.setState({ isChunkError: false });
      }
      return;
    }

    console.error('Error caught by boundary:', error, errorInfo);

    // Add breadcrumb for error context
    addBreadcrumb(
      'React Error Boundary triggered',
      'ui.error',
      {
        componentStack: errorInfo.componentStack,
        errorBoundary: this.constructor.name,
      }
    );
    
    // Capture error with Sentry
    captureError(error, {
      errorInfo,
      errorBoundary: this.constructor.name,
      react: {
        componentStack: errorInfo.componentStack,
      }
    });
  }

  render() {
    if (this.state.hasError) {
      // Stale lazy-chunk import: a reload is already in flight (or imminent).
      // Show the same branded loader as a normal route transition so navigating
      // right after a deploy doesn't flash a full-screen error page.
      if (this.state.isChunkError) {
        return <BrandedLoader label="Updating to the latest version…" />;
      }

      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="min-h-screen flex items-center justify-center p-4">
          <div className="text-center max-w-md">
            <AlertCircle className="h-12 w-12 text-destructive mx-auto mb-4" />
            <h2 className="text-2xl font-bold mb-2">Something went wrong</h2>
            <p className="text-muted-foreground mb-4">
              We're sorry, but something unexpected happened. Please try refreshing the page.
            </p>
            <Button onClick={() => window.location.reload()}>
              Refresh Page
            </Button>
            {process.env.NODE_ENV === 'development' && this.state.error && (
              <pre className="mt-4 text-left text-xs bg-secondary p-4 rounded-md overflow-auto">
                {this.state.error.toString()}
              </pre>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}